/**
 * Onboarding copy — single source of truth.
 *
 * Voice: terse, confident, lower-case fragments where natural.
 *
 * Mental model: we frame Takeover as **land + taxes** in user-facing copy.
 * Step 1 of each flow makes the bridge explicit ("each tile is a piece
 * of land on the board") so the metaphor is anchored before we lean
 * into "land", "plot", "landholder", and "taxes" through the rest of
 * the flow. Codebase identifiers stay as "tiles" — the rename is
 * scoped to onboarding for now.
 */

/**
 * Scene specs map 1:1 with rendering branches in `TourStage2D`.
 *
 * Player scenes drive the 5-step player tour; creator scenes drive the
 * 5-step creator intro. Each ID pins the visual treatment for that step
 * — see the `switch (scene)` inside `tour-stage/TourStage2D.tsx` for the
 * implementation of each branch.
 */
export type SceneId =
  // Player
  | 'yield-single'
  | 'buyout-grid'
  | 'price-oscillate'
  | 'empire'
  // Creator
  | 'creator-plots'
  | 'creator-taxes'
  | 'creator-value'
  | 'creator-trade'
  | 'creator-empire';

export interface OnboardingStep {
  id: string;
  title: string;
  body: string;
  /** Final-step button label. Other steps use the shared "Next". */
  cta?: string;
  scene: SceneId;
}

export const PLAYER_STEPS: readonly OnboardingStep[] = [
  // The tour opens straight on the yield beat. The board defaults to
  // 2D for everyone and 3D is an opt-in from the graphics menu, so
  // there's no view-mode interrupt up front any more.
  {
    id: 'yield',
    title: 'Boards pay yield to landholders',
    body: 'Each tile is a piece of land on the board, and each board can hold an unlimited set of yield sources. Hold land and earn a share of the yield the board generates.',
    scene: 'yield-single',
  },
  {
    id: 'for-sale',
    title: 'Every plot is for sale, always',
    body: 'Your job as a landholder is to price your land effectively. The price you set is the price that anyone can buy you out for.',
    scene: 'buyout-grid',
  },
  {
    id: 'self-assessed',
    title: 'You set the price, you pay the taxes',
    body: 'Whatever you say your land is worth, you pay weekly taxes on it. Price too high and taxes burn you out. Price too low and a neighbour buys you out.',
    scene: 'price-oscillate',
  },
  {
    id: 'empire',
    title: 'Price your land effectively',
    // Final step also surfaces the "this build is in beta" line so
    // returning visitors are reminded once per device that the previous
    // experience lives at classic.takeover.fun. The standalone beta
    // acknowledgment modal that used to chase the tour was retired in
    // favour of folding the copy into the tour itself.
    body: 'The game is finding a price where the value of your land is worth more to you than the cost of paying for it. Hold onto land that is valuable to you and grow your empire. Heads up — this build is in beta; the previous experience lives at classic.takeover.fun.',
    cta: 'Start playing',
    scene: 'empire',
  },
] as const;

export const CREATOR_STEPS: readonly OnboardingStep[] = [
  {
    id: 'plots',
    title: 'Your board is the land. Players hold the plots.',
    body: 'Each tile is a piece of land on your board with a proportional share of the yield that comes into it.',
    scene: 'creator-plots',
  },
  {
    id: 'taxes',
    title: 'You collect taxes on every plot in real-time',
    body: 'Taxes flow to you based on the value of your board. A higher board value commands more tax revenue.',
    scene: 'creator-taxes',
  },
  {
    id: 'value',
    title: 'Increase the value of your board',
    body: 'Creators can increase board value by adding yield sources to their board or offering perks to their land holders. Each board has a private chat for land holders to coordinate together.',
    scene: 'creator-value',
  },
  {
    id: 'trade',
    title: 'Earn from land trading',
    body: 'Every time a plot changes hands you earn 8% of the listing fee. The higher the turnover of land the higher the fees.',
    scene: 'creator-trade',
  },
  {
    id: 'empire',
    title: 'Build an empire',
    // Final step also surfaces the beta acknowledgment for creators,
    // matching the player flow's last-step folding. One in-tour reminder
    // per session instead of stacking a separate modal behind it.
    body: 'Boards are economic systems that are continuously priced by landholders. Coordinate and create value together to build an onchain empire. Heads up — this build is in beta; the previous experience lives at classic.takeover.fun.',
    cta: 'Start building',
    scene: 'creator-empire',
  },
] as const;

export const RECAP_COPY = {
  title: 'Your board is live',
  body: 'Share it, invite landholders, and watch the taxes start flowing in.',
  primary: 'Share link',
  secondary: 'Continue',
} as const;

export const OVERLAY_LABELS = {
  back: 'Back',
  next: 'Next',
  skip: 'Skip',
  dontShow: "Don't show again",
} as const;
